/* eslint-disable */
import React, { useRef } from 'react';
import styled from 'styled-components';
import { TextButton } from '@oneaudi/unified-web-components';
import type { Placeholder } from '@oneaudi/i18n-context';
import { useI18n } from '@oneaudi/i18n-context';
import { messages } from '../../../i18n/messages';
import { DEFAULT_NR_OF_RESULTS } from '../MobileLayout/MobileLayoutUtils';

export type Props = {
  onClick: () => void;
  displayResultsLength: number;
  totalNumOfResults: number;
};

function throttle<T extends (...args: never[]) => void>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let lastFunc: ReturnType<typeof setTimeout>;
  let lastRan: number;

  return function throttledFunction(...args: Parameters<T>) {
    if (!lastRan) {
      func(...args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(
        function timeoutFunction() {
          if (Date.now() - lastRan >= limit) {
            func(...args);
            lastRan = Date.now();
          }
        },
        limit - (Date.now() - lastRan),
      );
    }
  };
}

export const LoadMoreButton = ({ onClick, totalNumOfResults, displayResultsLength }: Props) => {
  const loadRef = useRef<HTMLDivElement>(null);
  const numOfDealers = getNumOfDealers(totalNumOfResults, displayResultsLength);

  function checkVisible(elm: HTMLDivElement | null) {
    if (elm) {
      const rect = elm.getBoundingClientRect();
      const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
      if (!(rect.bottom < 0 || rect.top - viewHeight >= 0)) {
        onClick();
      }
    }
  }

  const throttleCheckVisible = throttle(checkVisible, 1000);

  const numOfResults = useI18n(messages.load_more_dealers, {
    numOfDealers,
  } as Placeholder);

  React.useEffect(() => {
    if (loadRef.current) {
      window.addEventListener('scroll', () => {
        throttleCheckVisible(loadRef.current);
      });

      window.addEventListener('touchmove', () => {
        throttleCheckVisible(loadRef.current);
      });
    }

    return () => {
      if (loadRef.current) {
        window.removeEventListener('scroll', () => {
          throttleCheckVisible(loadRef.current);
        });
        window.removeEventListener('touchmove', () => {
          throttleCheckVisible(loadRef.current);
        });
      }
    };
  });

  return numOfDealers ? (
    <Container onClick={onClick} ref={loadRef}>
      <TextButton
        variant="primary"
        size="sm"
        onClick={onClick}
        data-test-id="mobile-layout-load-more-button"
      >
        {numOfResults}
      </TextButton>
    </Container>
  ) : null;
};

const getNumOfDealers = (totalNumOfResults: number, displayResultsLength: number): number => {
  const delta = totalNumOfResults - displayResultsLength;

  if (delta < DEFAULT_NR_OF_RESULTS) return delta;

  return DEFAULT_NR_OF_RESULTS;
};

const Container = styled.div.attrs({
  className: 'load-more-button',
  'data-test-id': 'mobile-layout-load-more-button',
})`
  width: 100%;
  display: flex;
  justify-content: center;
`;
